import React from "react";
import { Upload, ArrowRight, Loader2, RefreshCw, PlusCircle } from "lucide-react";
import { btnPrimary, btnGhost, importSourceLabel } from "./onboardShared";

/** Collect phase — paste / CSV-upload domains you already own. */
export function OwnedLane({ w }) {
  const invalid = w.ownedParsed.length - w.ownedValid.length;

  function onCsv(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => w.setOwnedText((prev) => (prev ? prev + "\n" : "") + String(reader.result || ""));
    reader.readAsText(file);
    e.target.value = "";
  }

  return (
    <div className="mt-5 space-y-5">
      <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="label-tiny">Domains you already own (one per line, comma, or CSV)</div>
          <label className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white cursor-pointer border border-zinc-700 hover:border-zinc-500 px-2.5 py-1.5 rounded-sm">
            <Upload size={13} /> Upload CSV
            <input data-testid="domains-owned-csv" type="file" accept=".csv,.txt" className="hidden" onChange={onCsv} />
          </label>
        </div>
        <textarea data-testid="domains-owned-textarea" value={w.ownedText} onChange={(e) => w.setOwnedText(e.target.value)} rows={6} placeholder={"example.com\nmysite.net\nanother.org"} className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono" />
        <div className="flex items-center justify-between mt-3">
          <div data-testid="domains-owned-parse-count" className="text-xs text-zinc-400">
            <b className="text-white">{w.ownedValid.length}</b> valid domain(s)
            {invalid > 0 && <span className="text-rose-400 ml-2">· {invalid} invalid (ignored)</span>}
          </div>
          <button data-testid="domains-owned-continue-btn" onClick={w.toReview} disabled={w.ownedValid.length === 0} className={btnPrimary}>Configure <ArrowRight size={15} /></button>
        </div>
      </div>
    </div>
  );
}

function InventoryStatus({ d }) {
  if (d.already_onboarded) return <span className="text-[11px] mono uppercase text-zinc-500">onboarded</span>;
  if (d.is_expired) return <span className="text-[11px] mono uppercase text-rose-400">expired</span>;
  return <span className="text-[11px] mono uppercase text-emerald-400">new</span>;
}

const INV_GRID = "grid grid-cols-[32px_1fr_120px_120px]";

/** Collect phase — Namecheap / Cloudflare Registrar inventory picker. */
export function ImportLane({ w }) {
  const { inv, lane, selected, selectedList, toggleDomain, toReview } = w;
  const source = importSourceLabel(lane);
  const inventory = inv.inventory;
  return (
    <div className="mt-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-400">{inventory ? <>{inventory.count} at {source} · <b className="text-emerald-400">{inventory.count - inventory.onboarded_count}</b> not yet onboarded</> : `Load your ${source} domains`}</div>
        <div className="flex items-center gap-2">
          <button data-testid="domains-import-selectall" onClick={() => w.setSelected(new Set(inv.newDomains()))} disabled={!inventory} className={btnGhost}><PlusCircle size={14} /> Select all new</button>
          <button data-testid="domains-import-refresh" onClick={() => inv.load(lane)} disabled={inv.loading} className={btnGhost}>{inv.loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh</button>
        </div>
      </div>
      <div className="border border-zinc-800 rounded-sm overflow-hidden">
        <div className={`${INV_GRID} bg-zinc-900/70 border-b border-zinc-800 label-tiny px-4 py-2`}><div></div><div>Domain</div><div>Expires</div><div>Status</div></div>
        <div className="divide-y divide-zinc-800 max-h-[420px] overflow-y-auto">
          {(inventory?.domains || []).map((d) => (
            <label key={d.domain} data-testid={`domains-import-row-${d.domain}`} className={`${INV_GRID} items-center px-4 py-2 text-sm ${d.already_onboarded ? "opacity-45" : "cursor-pointer hover:bg-zinc-900/40"}`}>
              <input type="checkbox" data-testid={`domains-import-select-${d.domain}`} disabled={d.already_onboarded} checked={selected.has(d.domain)} onChange={() => toggleDomain(d.domain)} />
              <span className="mono truncate">{d.domain}</span>
              <span className="mono text-xs text-zinc-500">{(d.expires_at || "").slice(0, 10)}</span>
              <span><InventoryStatus d={d} /></span>
            </label>
          ))}
          {!inventory && !inv.loading && <div className="px-4 py-10 text-center text-zinc-500 text-sm">Click Refresh to load your {lane === "import-cf" ? "Cloudflare Registrar" : "Namecheap account"} inventory.</div>}
          {inv.loading && <div className="px-4 py-10 text-center text-zinc-500 text-sm"><Loader2 size={16} className="animate-spin inline" /> Loading…</div>}
        </div>
        {inventory && (
          <div className="flex items-center justify-between border-t border-zinc-800 px-4 py-3">
            <div className="text-xs text-zinc-400">Selected <b className="text-white">{selectedList.length}</b></div>
            <button data-testid="domains-import-continue-btn" onClick={toReview} disabled={selectedList.length === 0} className={btnPrimary}>Configure {selectedList.length || ""} <ArrowRight size={15} /></button>
          </div>
        )}
      </div>
    </div>
  );
}
